import React, { useEffect } from 'react';
import { 
    Box,
    Card,  
    CardActionArea,
    CardContent, 
    Container,  
    Divider, 
    Grid, 
    Link,
    Typography } from '@material-ui/core';
import Stack from '@mui/material/Stack';
import { GitHub, Email, LinkedIn, School } from '@material-ui/icons';
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import CodeIcon from '@mui/icons-material/Code';
import MovieIcon from '@mui/icons-material/Movie';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import FindReplaceIcon from '@mui/icons-material/FindReplace';
import useStyles from './styles';
import Aos from 'aos';
import 'aos/dist/aos.css'; 

const App = () => {
    const classes = useStyles();

    useEffect(() =>{
        Aos.init({duration: 1000});
    }, []);  

    return (
    <main aria-label="Content">
        <Container maxWidth="lg" sx={{ py: 6 }} style={{ paddingTop: 20, }}>
            <Grid
                container
                spacing={3}
                alignItems="center"
                justifyContent="center"
            >
                {/* Picture section */}
                <Box
                    role="img"
                    aria-label="Profile picture"
                    sx={{
                    width: 300,
                    height: 460,
                    borderRadius: 5,
                    backgroundImage: `url(${require("./img/sneha_full_image.jpg")})`,
                    backgroundSize: "contain",
                    backgroundRepeat: "no-repeat",
                    boxShadow: 3,
                    backgroundPosition: "center",
                    display: "flex",
                    alignItems: "center",
                    }}
                />
                
                {/* Text section */}
                <Grid item xs={12} md={8}>
                    <Typography variant="h4" className={classes.headNames}>
                        Hi, I&apos;m Sneha Gathani!
                    </Typography>
                
                    <Box className="intro" sx={{ mt: 3 }}>
                        <Typography paragraph className={classes.allContent}>
                            I am a <b><i style={{ color: '#033f63' }}>Machine Learning Engineer</i></b> at{" "}
                            <Link href="https://experienceleague.adobe.com/en/docs/experience-platform/ai-assistant/home" target="_blank" rel="noreferrer" className={classes.a}>Adobe Experience Platform (AEP) AI</Link>, working{" "}
                            at the intersection of <i style={{ color: '#033f63' }}>machine learning (ML)</i> and <i style={{ color: '#033f63' }}>human-computer interaction (HCI)</i>. My work focuses on building <b><i style={{ color: '#033f63' }}>unsupervised, large language model (LLM)-driven systems</i></b> that proactively{" "} 
                            uncover hidden patterns in customer data and surface personalized, actionable business insights to support data-driven campaigns and strategic decision-making.
                            <br /><br />
                            I earned my Ph.D. in Computer Science from the{" "}
                            <Link href="https://www.cs.umd.edu/" target="_blank" rel="noreferrer" className={classes.a}>
                                University of Maryland, College Park
                            </Link>, where I was advised by{" "}
                            <a href="https://www.zcliu.org/" target="_blank" rel="noreferrer" className={classes.a}>
                                Dr. Leo Zhicheng Liu
                            </a> and was a part of the{" "}
                            <a href="https://hdi.cs.umd.edu/" target="_blank" rel="noreferrer" className={classes.a}>
                                Human-Data Interaction Group
                            </a> and{" "}
                            <a href="https://hcil.umd.edu/" target="_blank" rel="noreferrer" className={classes.a}>
                                HCIL
                            </a>. Prior to my Ph.D., I received a Master’s in Computer Science from University of Maryland, working with{" "}
                            <a href="https://www.cs.umd.edu/~leilani/bio.html" target="_blank" rel="noreferrer" className={classes.a}>
                                Prof. Leilani Battle
                            </a> in the 
                            <a href="https://battle-data-lab.cs.umd.edu/" target="_blank" rel="noreferrer" className={classes.a}>{" "}
                                BAttle Data Lab (BAD Lab)
                            </a> at the intersection of data visualization, databases, and HCI and a Bachelor’s in Computer Engineering from the{" "} 
                            <a href="https://pict.edu/" target="_blank" rel="noreferrer" className={classes.a}>
                                University of Pune, India
                            </a>, specializing in Computer Science.
                            <br /><br />
                            My research centers on building <b><i style={{ color: '#033f63' }}>AI-augmented decision-support systems</i></b> that integrate{" "} 
                            <b><i style={{ color: '#033f63' }}>ML models, LLMs</i></b>, and <b><i style={{ color: '#033f63' }}>interactive visual analytics workflows</i></b>{" "}
                            to help business stakeholders make fast, reliable, and transparent data-informed decisions.{" "} I have applied this research in industry through internships at{" "} 
                            <b><i style={{ color: '#033f63' }}>Sigma Computing</i></b>,{" "} 
                            <b><i style={{ color: '#033f63' }}>Tableau Research</i></b>, and{" "} 
                            <b><i style={{ color: '#033f63' }}>Microsoft Research</i></b>,{" "}
                            where I developed model-in-the-loop visual analytics decision systems, human-in-the-loop pipelines for automated insight generation, and LLM-augmented{" "}
                            tools that make advanced analytics more accessible, interpretable, and usable for real-world decision-making.
                        </Typography>
                    </Box>

                    <Divider sx={{ my: 3 }} style={{ marginTop: 10, }} />

                    {/* Contact + Docs */}
                    <Grid
                        container
                        spacing={1}
                        alignItems="center"
                        justifyContent="space-between"
                        style={{ paddingTop: 10, }}
                    >
                    <Grid item>
                        <Stack direction="row" spacing={2} alignItems="center">
                            <Stack direction="row" alignItems="center">
                                <Email className={classes.contactIcon} />   
                                <Stack
                                    direction="row"
                                    alignItems="center"
                                    className={classes.contactItem}
                                    component={Link}
                                    href="mailto:sgathani@umd.edu"
                                    style={{ paddingLeft: 5, }}
                                >
                                    <span className={classes.contactText} style={{ textJustify: 'center', }}>sgathani@umd.edu</span>
                                </Stack>
                            </Stack>

                            <Stack direction="row" alignItems="center">
                                <LinkedIn className={classes.contactIcon} />
                                <Stack
                                    direction="row"
                                    alignItems="center"
                                    className={classes.contactItem}
                                    component={Link}
                                    href="https://www.linkedin.com/in/sneha-gathani/"
                                    style={{ paddingLeft: 5, }}
                                >
                                    <span className={classes.contactText} style={{ textJustify: 'center', paddingLeft: '-10px', }}>sneha-gathani</span>
                                </Stack>
                            </Stack>

                            <Stack direction="row" alignItems="center">
                                <GitHub className={classes.contactIcon} />
                                <Stack
                                    direction="row"
                                    alignItems="center"
                                    className={classes.contactItem}
                                    component={Link}
                                    href="https://github.com/sneha-gathani"
                                    style={{ paddingLeft: 5, }}
                                >
                                    <span className={classes.contactText} style={{ textJustify: 'center', paddingLeft: '-10px', }}>sneha-gathani</span>
                                </Stack>
                            </Stack>
                        </Stack>
                    </Grid>

                    <Grid item>
                        <Stack direction="row" spacing={2}>
                            <Stack direction="row" alignItems="center">
                                <School className={classes.contactIcon} />
                                <Stack
                                    direction="row"
                                    alignItems="center"
                                    className={classes.contactItem}
                                    component={Link}
                                    href="https://scholar.google.com/citations?user=n3f44dkAAAAJ&hl=en"
                                    style={{ paddingLeft: 5, }}
                                >
                                    <span className={classes.contactText} style={{ textJustify: 'center', paddingLeft: '-10px', }}>Google Scholar</span>
                                </Stack>
                            </Stack>
                            <Stack direction="row" alignItems="center">
                                <PictureAsPdfIcon className={classes.contactIcon} />
                                <Stack
                                    direction="row"
                                    alignItems="center"
                                    className={classes.contactItem}
                                    component={Link}
                                    href="https://drive.google.com/file/d/1S3wEJNWWKtJnaR5xBfcu7KZsE7_0bfmR/view?usp=sharing"
                                    style={{ paddingLeft: 5, }}
                                >
                                    <span className={classes.contactText} style={{ textJustify: 'center', paddingLeft: '-10px', }}>CV</span>
                                </Stack>
                            </Stack>
                        </Stack>
                    </Grid>
                </Grid>
                </Grid>
            </Grid>
        </Container>

        <Container maxWidth="lg" sx={{ py: 6 }} style={{ paddingTop: 40, }}>
            <Grid
                container
                spacing={4}
                alignItems="center"
                justifyContent="center"
            >                
                {/* Text section */}
                <Grid item xs={12} md={8}>
                    <Typography variant="h4" className={classes.headNames}>
                        Dissertation Research
                    </Typography>

                    <Box className="intro" sx={{ mt: 3 }} style={{ marginTop: 15, }}>
                        <Typography paragraph className={classes.allContent}>
                            My Ph.D. dissertation, titled <b><i style={{ color: '#033f63' }}>Empowering Business Professionals with Interactive What-if Analysis: From Conceptual Framework to LLM-Powered Automation</i></b>,
                            focuses on building <b><i style={{ color: '#033f63' }}>AI-augmented decision-support systems</i></b> that help business stakeholders move beyond manual data analysis to explore what-if scenarios, test hypotheses, and make faster, more informed decisions.
                            I approach this problem by integrating techniques from three core areas:<br />
                            • <b><i style={{ color: '#033f63' }}>Interactive data and visual analytics:</i></b> Designing no- and low-code interfaces that allow users to explore compare, and reason about data through interactive visual workflows.<br />
                            • <b><i style={{ color: '#033f63' }}>ML- and LLM-powered analytics:</i></b> Leveraging predictive ML models and LLMs to enable adaptive, context-aware reasoning that responds to evolving user goals.<br />
                            • <b><i style={{ color: '#033f63' }}>Natural language and specification languages:</i></b> Developing intermediate representations--such as specification languages--that let users express analytical intent in plain natural language, while maintaining transparency, control, and trust in automated analyses.<br />
                            My goal is to empower a broad range of <b><i style={{ color: '#033f63' }}>business stakeholders</i></b>—from business users (e.g., sales, marketing, product, and operations managers) to business analysts—to conduct sophisticated analyses without requiring technical expertise.<br /><br />

                            <Typography variant="h8" style={{ fontSize: 24, color: '#DF825F', fontWeight: 'bold', }}>How I do this?</Typography><br />
                            My work follows a human-centered, systems-oriented approach:<br />
                            1. <b><i style={{ color: '#033f63' }}>Understanding real decision-making workflows:</i></b> I study how business decisions are made in practice through interviews, observations, and qualitative analysis to uncover user goals, constraints, and challenges in existing analytics tools.<br />
                            2. <b><i style={{ color: '#033f63' }}>Embedding ML into interactive, no-code visual systems:</i></b> I design and build interactive systems that integrate predictive ML models, generative reasoning, and adaptive workflows, enabling users to simulate scenarios, compare outcomes, and iterate as business conditions change.<br />
                            3. <b><i style={{ color: '#033f63' }}>Making AI usable, transparent, and trustworthy:</i></b> I develop systems that translate natural language analytical questions directly into interactive analyses and interfaces and analyses using using agentic workflows via specification languages. This reduces the burden of manual analysis and reporting while giving users visibility into—and control over—LLM-driven automation.
                        </Typography>
                    </Box>
                </Grid>

                {/* Picture section */}
                <Box
                    role="img"
                    aria-label="Profile picture"
                    sx={{
                    width: 380,
                    height: 500,
                    borderRadius: 5,
                    backgroundImage: `url(${require("./img/research_interest.png")})`,
                    backgroundSize: "contain",
                    backgroundRepeat: "no-repeat",
                    boxShadow: 3,
                    backgroundPosition: "center",
                    display: "flex",
                    alignItems: "center",
                    }}
                />
            </Grid>
        </Container>

        <Container maxWidth="lg" sx={{ py: 6 }} style={{ paddingTop: 60, }}>
            <Typography variant="h4" className={classes.headNames}>
                Publications
            </Typography>

            <Grid container spacing={3} className="grid featured" style={{ paddingTop: 10, }}>
                {/* Item */}
                <Grid item xs={12} sm={6} md={4}>
                    <Card elevation={2} sx={{ height: "100%" }}>
                        <CardActionArea
                            href="https://drive.google.com/file/d/1yp0RC7tHV6hEGAWrO-Mpszo-a9bymtKB/view?usp=sharing"
                            target="_blank"
                            rel="noreferrer"
                        >
                            {/* <Box
                            className="thumb"
                            sx={{
                                // pt: "64.25%", // 16:9
                                width: 305,
                                height: 180,
                                backgroundImage: `url(${require("./img/groot.png")})`,
                                // "url(/assets/vega-lite/teaser.png)"
                                backgroundSize: "cover",
                                backgroundPosition: "center",
                            }}
                            /> */}
                            <Box
                                sx={{
                                    width: 395,
                                    height: 140,
                                    display: "block",
                                    justifyContent: "center",
                                    alignContent: 'center',
                                }}
                                >
                                <img
                                    src={require("./img/ds.png")}
                                    // "url(/assets/vega-lite/teaser.png)"
                                    alt="What-if Analysis for Business Professionals"
                                    style={{ width: 395, height: 140, }}
                                />
                            </Box>
                            <CardContent>
                                <Typography className={classes.title}>
                                    What-if Analysis for Business Professionals: Current Practices and Future Opportunities
                                </Typography>
                                <Typography className={classes.authors}>
                                    <b className={classes.bold}>Sneha Gathani</b>, Zhicheng Liu, Peter J. Haas, Çağatay Demiralp
                                </Typography>
                                <Typography sx={{ fontSize: '16px', display: 'flex', alignItems: 'center' }} style={{ paddingTop: '5px', }}>
                                    <AutoAwesomeIcon sx={{ fontSize: '16px', color: '#DF825F', }} /><b style={{ fontSize: '18px', fontWeight: 'bold', marginLeft: '4px' }}>CHI 2025</b>
                                </Typography>
                                <Typography className={classes.subtitle} style={{ paddingTop: '16px', }}>
                                    A two-part user study with business professionals to (1) examine existing what-if analysis techniques employed, tools used, and challenges faced, and (2) evaluate their practical use of representative what-if analysis techniques identified in the first study.
                                <Typography className={classes.subtitleHide}>This is hidden, this is hidden, this is hidden, this is hidden, this is hidden.</Typography>
                                </Typography>
                                <Stack direction="row" spacing={2} alignItems="center">
                                    <Stack direction="row" alignItems="center">
                                        <PictureAsPdfIcon className={classes.contactIcon} />   
                                        <Stack
                                            direction="row"
                                            alignItems="center"
                                            className={classes.contactItem}
                                            component={Link}
                                            href="https://dl.acm.org/doi/pdf/10.1145/3706598.3713672"
                                            style={{ paddingLeft: 5, }}
                                        >
                                            <span className={classes.iconPill} style={{ textJustify: 'center', }}>PDF</span>
                                        </Stack>
                                    </Stack>

                                    <Stack direction="row" alignItems="center">
                                        <PlayArrowIcon className={classes.contactIcon} />
                                        <Stack
                                            direction="row"
                                            alignItems="center"
                                            className={classes.contactItem}
                                            component={Link}
                                            href="https://drive.google.com/file/d/1yp0RC7tHV6hEGAWrO-Mpszo-a9bymtKB/view?usp=sharing"
                                            style={{ paddingLeft: 5, }}
                                        >
                                            <span className={classes.iconPill} style={{ textJustify: 'center', paddingLeft: '-10px', }}>Demo</span>
                                        </Stack>
                                    </Stack>

                                    {/* <Stack direction="row" alignItems="center">
                                        <MovieIcon className={classes.contactIcon} />
                                        <Stack
                                            direction="row"
                                            alignItems="center"
                                            className={classes.contactItem}
                                            component={Link}
                                            href=""
                                            style={{ paddingLeft: 5, }}
                                        >
                                            <span className={classes.iconPill} style={{ textJustify: 'center', paddingLeft: '-10px', }}>Talk</span>
                                        </Stack>
                                    </Stack> */}
                                </Stack>
                            </CardContent>
                        </CardActionArea>
                    </Card>
                </Grid>

                {/* Item */}
                <Grid item xs={12} sm={6} md={4}>
                    <Card elevation={2} sx={{ height: "100%" }}>
                        <CardActionArea
                            href="https://drive.google.com/file/d/1i1ap-BpetMmydGZrrg2ogYrDfUSIsGKY/view?usp=sharing"
                            target="_blank"
                            rel="noreferrer"
                        >
                            {/* <Box
                            className="thumb"
                            sx={{
                                // pt: "64.25%", // 16:9
                                width: 305,
                                height: 180,
                                backgroundImage: `url(${require("./img/groot.png")})`,
                                // "url(/assets/vega-lite/teaser.png)"
                                backgroundSize: "cover",
                                backgroundPosition: "center",
                            }}
                            /> */}
                            <Box
                                sx={{
                                    width: 395,
                                    height: 140,
                                    display: "block",
                                    justifyContent: "center",
                                    alignContent: 'center',
                                }}
                                >
                                <img
                                    src={require("./img/groot.png")}
                                    // "url(/assets/vega-lite/teaser.png)"
                                    alt="Groot"
                                    style={{ width: 395, height: 140, }}
                                />
                            </Box>
                            <CardContent>
                                <Typography className={classes.title}>
                                    Groot: An Interface for Editing and Configuring Automated Data Insights
                                </Typography>
                                <Typography className={classes.authors}>
                                    <b className={classes.bold}>Sneha Gathani</b>, Anamaria Crisan, Vidya Setlur, Arjun Srinivasan
                                </Typography>
                                <Typography sx={{ fontSize: '16px', display: 'flex', alignItems: 'center' }} style={{ paddingTop: '5px', }}>
                                    <AutoAwesomeIcon sx={{ fontSize: '16px', color: '#DF825F', }} /><b style={{ fontSize: '18px', fontWeight: 'bold', marginLeft: '4px' }}>VIS 2024</b>
                                </Typography>
                                <Typography className={classes.subtitle} style={{ paddingTop: '16px', }}>
                                    A system that allows users to proactively configure and refine automated data insight engines.
                                <Typography className={classes.subtitleHide}>This is hidden, this is hidden, this is hidden, this is hidden, this is hidden. This is hidden, this is hidden, this is hidden, this is hidden, this is hidden. This is hidden, this is hidden, this is hidden, this is hidden, this is hidden.</Typography>
                                </Typography>
                                <Stack direction="row" spacing={2} alignItems="center">
                                    <Stack direction="row" alignItems="center">
                                        <PictureAsPdfIcon className={classes.contactIcon} />   
                                        <Stack
                                            direction="row"
                                            alignItems="center"
                                            className={classes.contactItem}
                                            component={Link}
                                            href="https://drive.google.com/file/d/1wC8t2HwN4lUG1T64p0NlHvFFLLCWZX5n/view?usp=sharing"
                                            style={{ paddingLeft: 5, }}
                                        >
                                            <span className={classes.iconPill} style={{ textJustify: 'center', }}>PDF</span>
                                        </Stack>
                                    </Stack>

                                    <Stack direction="row" alignItems="center">
                                        <PlayArrowIcon className={classes.contactIcon} />
                                        <Stack
                                            direction="row"
                                            alignItems="center"
                                            className={classes.contactItem}
                                            component={Link}
                                            href="https://drive.google.com/file/d/1i1ap-BpetMmydGZrrg2ogYrDfUSIsGKY/view?usp=sharing"
                                            style={{ paddingLeft: 5, }}
                                        >
                                            <span className={classes.iconPill} style={{ textJustify: 'center', paddingLeft: '-10px', }}>Demo</span>
                                        </Stack>
                                    </Stack>

                                    <Stack direction="row" alignItems="center">
                                        <MovieIcon className={classes.contactIcon} />
                                        <Stack
                                            direction="row"
                                            alignItems="center"
                                            className={classes.contactItem}
                                            component={Link}
                                            href="https://www.youtube.com/watch?v=pqb9IsoJKWA"
                                            style={{ paddingLeft: 5, }}
                                        >
                                            <span className={classes.iconPill} style={{ textJustify: 'center', paddingLeft: '-10px', }}>Talk</span>
                                        </Stack>
                                    </Stack>
                                </Stack>
                            </CardContent>
                        </CardActionArea>
                    </Card>
                </Grid>

                {/* Item */}
                <Grid item xs={12} sm={6} md={4}>
                    <Card elevation={2} sx={{ height: "100%" }}>
                        <CardActionArea
                            href="https://arxiv.org/pdf/2201.03740.pdf"
                            target="_blank"
                            rel="noreferrer"
                        >
                            {/* <Box
                            className="thumb"
                            sx={{
                                // pt: "64.25%", // 16:9
                                width: 305,
                                height: 180,
                                backgroundImage: `url(${require("./img/groot.png")})`,
                                // "url(/assets/vega-lite/teaser.png)"
                                backgroundSize: "cover",
                                backgroundPosition: "center",
                            }}
                            /> */}
                            <Box
                                sx={{
                                    width: 395,
                                    height: 140,
                                    display: "block",
                                    justifyContent: "center",
                                    alignContent: 'center',
                                }}
                                >
                                <img
                                    src={require("./img/grammatic_embeddings.png")}
                                    // "url(/assets/vega-lite/teaser.png)"
                                    alt="Visualization Taxonomies to Interaction Logs"
                                    style={{ width: 395, height: 140, }}
                                />
                            </Box>
                            <CardContent>
                                <Typography className={classes.title}>
                                    A Grammar-Based Approach for Applying Visualization Taxonomies to Interaction Logs
                                </Typography>
                                <Typography className={classes.authors}>
                                    <b className={classes.bold}>Sneha Gathani</b>, Shayan Monadjemi, Alvitta Ottley, Leilani Battle
                                </Typography>
                                <Typography sx={{ fontSize: '16px', display: 'flex', alignItems: 'center' }} style={{ paddingTop: '5px', }}>
                                    <AutoAwesomeIcon sx={{ fontSize: '16px', color: '#DF825F', }} /><b style={{ fontSize: '18px', fontWeight: 'bold', marginLeft: '4px' }}>EuroVis 2022</b>
                                </Typography>
                                <Typography className={classes.subtitle} style={{ paddingTop: '16px', }}>
                                    Reformulating theoretical visualization task taxonomies as regular grammars to make them more actionable for empirical interaction log analysis.
                                <Typography className={classes.subtitleHide}>This is hidden, this is hidden, this is hidden, this is hidden, this is hidden. this is hidden, this is hidden. This is hidden, this is hidden, this is hidden, this is hidden, this is hidden.</Typography>
                                </Typography>
                                <Stack direction="row" spacing={2} alignItems="center">
                                    <Stack direction="row" alignItems="center">
                                        <PictureAsPdfIcon className={classes.contactIcon} />   
                                        <Stack
                                            direction="row"
                                            alignItems="center"
                                            className={classes.contactItem}
                                            component={Link}
                                            href="https://arxiv.org/pdf/2201.03740.pdf"
                                            style={{ paddingLeft: 5, }}
                                        >
                                            <span className={classes.iconPill} style={{ textJustify: 'center', }}>PDF</span>
                                        </Stack>
                                    </Stack>

                                    <Stack direction="row" alignItems="center">
                                        <CodeIcon className={classes.contactIcon} />
                                        <Stack
                                            direction="row"
                                            alignItems="center"
                                            className={classes.contactItem}
                                            component={Link}
                                            href="https://osf.io/s3ucr/?view_only=aa0d8632ad1c4fe09faea9869fd5b977"
                                            style={{ paddingLeft: 5, }}
                                        >
                                            <span className={classes.iconPill} style={{ textJustify: 'center', paddingLeft: '-10px', }}>Code</span>
                                        </Stack>
                                    </Stack>

                                    <Stack direction="row" alignItems="center">
                                        <MovieIcon className={classes.contactIcon} />
                                        <Stack
                                            direction="row"
                                            alignItems="center"
                                            className={classes.contactItem}
                                            component={Link}
                                            href="https://www.youtube.com/watch?v=t7PVqvh0iQ4&list=PLSlLplD3pBWjPUtD5sU8qPmCkN_nn8ZZj&t=3105s"
                                            style={{ paddingLeft: 5, }}
                                        >
                                            <span className={classes.iconPill} style={{ textJustify: 'center', paddingLeft: '-10px', }}>Talk</span>
                                        </Stack>
                                    </Stack>
                                </Stack>
                            </CardContent>
                        </CardActionArea>
                    </Card>
                </Grid>
            </Grid>

            <Grid container spacing={3} className="grid featured" style={{ paddingTop: 10, }}>
                {/* Item */}
                <Grid item xs={12} sm={6} md={4}>
                    <Card elevation={2} sx={{ height: "100%" }}>
                        <CardActionArea
                            href="https://drive.google.com/file/d/1PZqyPjd99-HmsTDXZOJzEVVJJIHMSO82/view?usp=sharing"
                            target="_blank"
                            rel="noreferrer"
                        >
                            {/* <Box
                            className="thumb"
                            sx={{
                                // pt: "64.25%", // 16:9
                                width: 305,
                                height: 180,
                                backgroundImage: `url(${require("./img/groot.png")})`,
                                // "url(/assets/vega-lite/teaser.png)"
                                backgroundSize: "cover",
                                backgroundPosition: "center",
                            }}
                            /> */}
                            <Box
                                sx={{
                                    width: 395,
                                    height: 140,
                                    display: "block",
                                    justifyContent: "center",
                                    alignContent: 'center',
                                }}
                                >
                                <img
                                    src={require("./img/functionalities.png")}
                                    // "url(/assets/vega-lite/teaser.png)"
                                    alt="Groot"
                                    style={{ width: 395, height: 140, }}
                                />
                            </Box>
                            <CardContent>
                                <Typography className={classes.title}>
                                    Augmenting Decision Making via Interactive What-If Analysis
                                </Typography>
                                <Typography className={classes.titleHide}>This is hidden, This is</Typography>
                                <Typography className={classes.authors}>
                                    <b className={classes.bold}>Sneha Gathani</b>, Madelon Hulsebos, James Gale, Peter J. Haas, Çağatay Demiralp
                                </Typography>
                                <Typography sx={{ fontSize: '16px', display: 'flex', alignItems: 'center' }} style={{ paddingTop: '5px', }}>
                                    <AutoAwesomeIcon sx={{ fontSize: '16px', color: '#DF825F', }} /><b style={{ fontSize: '18px', fontWeight: 'bold', marginLeft: '4px' }}>CIDR 2022</b>
                                </Typography>
                                <Typography className={classes.subtitle} style={{ paddingTop: '16px', }}>
                                    Understanding the purpose of business data analysis and operationalizing it within an interactive visual data analysis system, SystemD, to enable business users to experiment with the data by asking what-if questions.
                                <Typography className={classes.subtitleHide}>This is hidden, this is is is hidden, this i is is hidden, this i is is hidden, this i is is hidden, this i is is hidden, this i jsdfoij sdjfjsdpjfpjsdpo hidden, this is hidden, this is hidden, this is hidden.</Typography>
                                </Typography>
                                <Stack direction="row" spacing={2} alignItems="center">
                                    <Stack direction="row" alignItems="center">
                                        <PictureAsPdfIcon className={classes.contactIcon} />   
                                        <Stack
                                            direction="row"
                                            alignItems="center"
                                            className={classes.contactItem}
                                            component={Link}
                                            href="https://www.cidrdb.org/cidr2022/papers/p49-gathani.pdf"
                                            style={{ paddingLeft: 5, }}
                                        >
                                            <span className={classes.iconPill} style={{ textJustify: 'center', }}>PDF</span>
                                        </Stack>
                                    </Stack>

                                    <Stack direction="row" alignItems="center">
                                        <PlayArrowIcon className={classes.contactIcon} />
                                        <Stack
                                            direction="row"
                                            alignItems="center"
                                            className={classes.contactItem}
                                            component={Link}
                                            href="https://drive.google.com/file/d/1PZqyPjd99-HmsTDXZOJzEVVJJIHMSO82/view?usp=sharing"
                                            style={{ paddingLeft: 5, }}
                                        >
                                            <span className={classes.iconPill} style={{ textJustify: 'center', paddingLeft: '-10px', }}>Demo</span>
                                        </Stack>
                                    </Stack>

                                    <Stack direction="row" alignItems="center">
                                        <MovieIcon className={classes.contactIcon} />
                                        <Stack
                                            direction="row"
                                            alignItems="center"
                                            className={classes.contactItem}
                                            component={Link}
                                            href="https://www.youtube.com/watch?v=an-oTVQzHT0"
                                            style={{ paddingLeft: 5, }}
                                        >
                                            <span className={classes.iconPill} style={{ textJustify: 'center', paddingLeft: '-10px', }}>Talk</span>
                                        </Stack>
                                    </Stack>
                                </Stack>
                            </CardContent>
                        </CardActionArea>
                    </Card>
                </Grid>

                {/* Item */}
                <Grid item xs={12} sm={6} md={4}>
                    <Card elevation={2} sx={{ height: "100%" }}>
                        <CardActionArea
                            href="https://www.cidrdb.org/cidr2022/papers/a48-hulsebos.pdf"
                            target="_blank"
                            rel="noreferrer"
                        >
                            {/* <Box
                            className="thumb"
                            sx={{
                                // pt: "64.25%", // 16:9
                                width: 305,
                                height: 180,
                                backgroundImage: `url(${require("./img/groot.png")})`,
                                // "url(/assets/vega-lite/teaser.png)"
                                backgroundSize: "cover",
                                backgroundPosition: "center",
                            }}
                            /> */}
                            <Box
                                sx={{
                                    width: 395,
                                    height: 140,
                                    display: "block",
                                    justifyContent: "center",
                                    alignContent: 'center',
                                }}
                                >
                                <img
                                    src={require("./img/sigmaTyper.png")}
                                    // "url(/assets/vega-lite/teaser.png)"
                                    alt="Groot"
                                    style={{ width: 395, height: 140, }}
                                />
                            </Box>
                            <CardContent>
                                <Typography className={classes.title}>
                                    Making Table Understanding Work in Practice
                                </Typography>
                                <Typography className={classes.titleHide}>This is hidden, This is</Typography>
                                <Typography className={classes.authors}>
                                    Madelon Hulsebos, <b className={classes.bold}>Sneha Gathani</b>, James Gale, Isil Dillig, Paul Groth, Çağatay Demiralp
                                </Typography>
                                <Typography sx={{ fontSize: '16px', display: 'flex', alignItems: 'center' }} style={{ paddingTop: '5px', }}>
                                    <AutoAwesomeIcon sx={{ fontSize: '16px', color: '#DF825F', }} /><b style={{ fontSize: '18px', fontWeight: 'bold', marginLeft: '4px' }}>CIDR 2022</b>
                                </Typography>
                                <Typography className={classes.subtitle} style={{ paddingTop: '16px', }}>
                                    Learning what do we need for table understanding models to work in practice and develop the SigmaTyper system to infer labeling functions from table based on user's feedback.
                                <Typography className={classes.subtitleHide}>This is hidden, this is hidden, this is hidden, this is hidden, this is hidden. This is hidden, this is hidden, this is hidden, this is hidden, this is hidden. This is hidden, this is hidden, this is hidden, this is hidden, this is hidden.</Typography>
                                </Typography>
                                <Stack direction="row" spacing={2} alignItems="center">
                                    <Stack direction="row" alignItems="center">
                                        <PictureAsPdfIcon className={classes.contactIcon} />   
                                        <Stack
                                            direction="row"
                                            alignItems="center"
                                            className={classes.contactItem}
                                            component={Link}
                                            href="https://www.cidrdb.org/cidr2022/papers/a48-hulsebos.pdf"
                                            style={{ paddingLeft: 5, }}
                                        >
                                            <span className={classes.iconPill} style={{ textJustify: 'center', }}>PDF</span>
                                        </Stack>
                                    </Stack>

                                    <Stack direction="row" alignItems="center">
                                        <MovieIcon className={classes.contactIcon} />
                                        <Stack
                                            direction="row"
                                            alignItems="center"
                                            className={classes.contactItem}
                                            component={Link}
                                            href="https://www.youtube.com/watch?v=-BE5rWNMXnU"
                                            style={{ paddingLeft: 5, }}
                                        >
                                            <span className={classes.iconPill} style={{ textJustify: 'center', paddingLeft: '-10px', }}>Talk</span>
                                        </Stack>
                                    </Stack>
                                </Stack>
                            </CardContent>
                        </CardActionArea>
                    </Card>
                </Grid>

                {/* Item */}
                <Grid item xs={12} sm={6} md={4}>
                    <Card elevation={2} sx={{ height: "100%" }}>
                        <CardActionArea
                            href="https://homes.cs.washington.edu/~leibatt/static/papers/gathani_debugging_CHI_2020.pdf"
                            target="_blank"
                            rel="noreferrer"
                        >
                            {/* <Box
                            className="thumb"
                            sx={{
                                // pt: "64.25%", // 16:9
                                width: 305,
                                height: 180,
                                backgroundImage: `url(${require("./img/groot.png")})`,
                                // "url(/assets/vega-lite/teaser.png)"
                                backgroundSize: "cover",
                                backgroundPosition: "center",
                            }}
                            /> */}
                            <Box
                                sx={{
                                    width: 395,
                                    height: 140,
                                    display: "block",
                                    justifyContent: "center",
                                    alignContent: 'center',
                                }}
                                >
                                <img
                                    src={require("./img/debugging.png")}
                                    // "url(/assets/vega-lite/teaser.png)"
                                    alt="Groot"
                                    style={{ width: 395, height: 140, }}
                                />
                            </Box>
                            <CardContent>
                                <Typography className={classes.title}>
                                    Debugging Database Queries: A Survey of Tools, Techniques, and Users
                                </Typography>
                                <Typography className={classes.authors}>
                                    <b className={classes.bold}>Sneha Gathani</b>, Peter Lim, Leilani Battle
                                </Typography>
                                <Typography className={classes.authorsHide}>This is hidden, This is hidden,</Typography>
                                <Typography sx={{ fontSize: '16px', display: 'flex', alignItems: 'center' }} style={{ paddingTop: '5px', }}>
                                    <AutoAwesomeIcon sx={{ fontSize: '16px', color: '#DF825F', }} /><b style={{ fontSize: '18px', fontWeight: 'bold', marginLeft: '4px' }}>CHI 2020</b>
                                </Typography>
                                <Typography className={classes.subtitle} style={{ paddingTop: '16px', }}>
                                    Interdisciplinary literature review and interview study to help understand database debugging strategies.
                                <Typography className={classes.subtitleHide}>This is hiddendden, Thi, This is hidden, This is hidden, This is hidden, This is hidden, This is hidden, This is hthis is hidden, this is hidden, this is hidden, this is hidden. this is hidden, this is hidden. This is hidden, this is hidden, this is hidden, this is hidden, this is hidden.</Typography>
                                </Typography>
                                <Stack direction="row" spacing={2} alignItems="center">
                                    <Stack direction="row" alignItems="center">
                                        <PictureAsPdfIcon className={classes.contactIcon} />   
                                        <Stack
                                            direction="row"
                                            alignItems="center"
                                            className={classes.contactItem}
                                            component={Link}
                                            href="https://homes.cs.washington.edu/~leibatt/static/papers/gathani_debugging_CHI_2020.pdf"
                                            style={{ paddingLeft: 5, }}
                                        >
                                            <span className={classes.iconPill} style={{ textJustify: 'center', }}>PDF</span>
                                        </Stack>
                                    </Stack>

                                    <Stack direction="row" alignItems="center">
                                        <MovieIcon className={classes.contactIcon} />
                                        <Stack
                                            direction="row"
                                            alignItems="center"
                                            className={classes.contactItem}
                                            component={Link}
                                            href="https://www.youtube.com/watch?v=auCLjsBi6zE"
                                            style={{ paddingLeft: 5, }}
                                        >
                                            <span className={classes.iconPill} style={{ textJustify: 'center', paddingLeft: '-10px', }}>Talk</span>
                                        </Stack>
                                    </Stack>
                                </Stack>
                            </CardContent>
                        </CardActionArea>
                    </Card>
                </Grid>
            </Grid>

            <Grid container spacing={3} className="grid featured" style={{ paddingTop: 10, }}>
                {/* Item */}
                <Grid item xs={12} sm={6} md={4}>
                    <Card elevation={2} sx={{ height: "100%" }}>
                        <CardActionArea
                            href=""
                            target="_blank"
                            rel="noreferrer"
                        >
                            {/* <Box
                            className="thumb"
                            sx={{
                                // pt: "64.25%", // 16:9
                                width: 305,
                                height: 180,
                                backgroundImage: `url(${require("./img/groot.png")})`,
                                // "url(/assets/vega-lite/teaser.png)"
                                backgroundSize: "cover",
                                backgroundPosition: "center",
                            }}
                            /> */}
                            <Box
                                sx={{
                                    width: 395,
                                    height: 140,
                                    display: "block",
                                    justifyContent: "center",
                                    alignContent: 'center',
                                }}
                                >
                                <img
                                    src={require("./img/praxa.png")}
                                    // "url(/assets/vega-lite/teaser.png)"
                                    alt="Praxa"
                                    style={{ width: 395, height: 140, }}
                                />
                            </Box>
                            <CardContent>
                                <Typography className={classes.title}>
                                    Praxa: A Standardized Approach to What-If Analysis
                                </Typography>
                                <Typography className={classes.titleHide}>This is hidden, Ts isThis is hidden, This isThis iss hidden, Ts isThis is hidden, This isThis is</Typography>
                                <Typography className={classes.authors}>
                                    <b className={classes.bold}>Sneha Gathani</b>, Kevin Li, Raghav Thind, Sirui Zeng, Matthew Xu, Peter J. Haas, Çağatay Demiralp, Zhicheng Liu
                                </Typography>
                                <Typography sx={{ fontSize: '16px', display: 'flex', alignItems: 'center' }} style={{ paddingTop: '5px', }}>
                                    <FindReplaceIcon sx={{ fontSize: '16px', color: '#DF825F', }} /><b style={{ fontSize: '18px', fontWeight: 'bold', marginLeft: '4px' }}>Under Review: TVCG 2026</b>
                                </Typography>
                                <Typography className={classes.subtitle} style={{ paddingTop: '16px', }}>
                                    Formulated a component-based framework standardizing various what-if analysis techniques by conducting literature review and qualitative analysis of 145+ visual analytics works.
                                <Typography className={classes.subtitleHide}>This is hidden, this is is is hidden, this i is is hidden, this i is is hidden, this i is is hidden, this i is is hidden, this i jsdfoij sdjfjsdpjfpjsdpo hidden, this is hidden, this is hidden, this is hidden.</Typography>
                                </Typography>
                                {/* <Stack direction="row" spacing={2} alignItems="center">
                                    <Stack direction="row" alignItems="center">
                                        <PictureAsPdfIcon className={classes.contactIcon} />   
                                        <Stack
                                            direction="row"
                                            alignItems="center"
                                            className={classes.contactItem}
                                            component={Link}
                                            href="https://www.cidrdb.org/cidr2022/papers/p49-gathani.pdf"
                                            style={{ paddingLeft: 5, }}
                                        >
                                            <span className={classes.iconPill} style={{ textJustify: 'center', }}>PDF</span>
                                        </Stack>
                                    </Stack>

                                    <Stack direction="row" alignItems="center">
                                        <PlayArrowIcon className={classes.contactIcon} />
                                        <Stack
                                            direction="row"
                                            alignItems="center"
                                            className={classes.contactItem}
                                            component={Link}
                                            href="https://drive.google.com/file/d/1PZqyPjd99-HmsTDXZOJzEVVJJIHMSO82/view?usp=sharing"
                                            style={{ paddingLeft: 5, }}
                                        >
                                            <span className={classes.iconPill} style={{ textJustify: 'center', paddingLeft: '-10px', }}>Demo</span>
                                        </Stack>
                                    </Stack>

                                    <Stack direction="row" alignItems="center">
                                        <MovieIcon className={classes.contactIcon} />
                                        <Stack
                                            direction="row"
                                            alignItems="center"
                                            className={classes.contactItem}
                                            component={Link}
                                            href="https://www.youtube.com/watch?v=an-oTVQzHT0"
                                            style={{ paddingLeft: 5, }}
                                        >
                                            <span className={classes.iconPill} style={{ textJustify: 'center', paddingLeft: '-10px', }}>Talk</span>
                                        </Stack>
                                    </Stack>
                                </Stack> */}
                            </CardContent>
                        </CardActionArea>
                    </Card>
                </Grid>

                <Grid item xs={12} sm={6} md={4}>
                    <Card elevation={2} sx={{ height: "100%" }}>
                        <CardActionArea
                            href=""
                            target="_blank"
                            rel="noreferrer"
                        >
                            {/* <Box
                            className="thumb"
                            sx={{
                                // pt: "64.25%", // 16:9
                                width: 305,
                                height: 180,
                                backgroundImage: `url(${require("./img/groot.png")})`,
                                // "url(/assets/vega-lite/teaser.png)"
                                backgroundSize: "cover",
                                backgroundPosition: "center",
                            }}
                            /> */}
                            <Box
                                sx={{
                                    width: 395,
                                    height: 140,
                                    display: "block",
                                    justifyContent: "center",
                                    alignContent: 'center',
                                }}
                                >
                                <img
                                    src={require("./img/nl_wia.png")}
                                    // "url(/assets/vega-lite/teaser.png)"
                                    alt="WhIf"
                                    style={{ width: 395, height: 140, }}
                                />
                            </Box>
                            <CardContent>
                                <Typography className={classes.title}>
                                    From Natural Language to Interactive What-If Interfaces via LLM-Generated Declarative Specifications: Promise and Challenges
                                </Typography>
                                <Typography className={classes.authors}>
                                    <b className={classes.bold}>Sneha Gathani</b>, Sirui Zeng, Diya Patel, Dan Marshall, Çağatay Demiralp, Steven Drucker, Zhicheng Liu
                                </Typography>
                                <Typography sx={{ fontSize: '16px', display: 'flex', alignItems: 'center' }} style={{ paddingTop: '5px', }}>
                                    <FindReplaceIcon sx={{ fontSize: '16px', color: '#DF825F', }} /><b style={{ fontSize: '18px', fontWeight: 'bold', marginLeft: '4px' }}>Under Review: VIS 2026</b>
                                </Typography>
                                <Typography className={classes.subtitle} style={{ paddingTop: '16px', }}>
                                    An approach translating natural language what-if questions to interactive visual interfaces via intermediate representation like Praxa Specification Language using LLMs and agentic workflows.
                                <Typography className={classes.subtitleHide}>This is hidden, this s i is is hidden, this i is is hidden, this i jsdfoij sdjfjsdpjfpjsdpo hidden, this is hidden, this is hidden, this is hidden.</Typography>
                                </Typography>
                                {/* <Stack direction="row" spacing={2} alignItems="center">
                                    <Stack direction="row" alignItems="center">
                                        <PictureAsPdfIcon className={classes.contactIcon} />   
                                        <Stack
                                            direction="row"
                                            alignItems="center"
                                            className={classes.contactItem}
                                            component={Link}
                                            href="https://www.cidrdb.org/cidr2022/papers/p49-gathani.pdf"
                                            style={{ paddingLeft: 5, }}
                                        >
                                            <span className={classes.iconPill} style={{ textJustify: 'center', }}>PDF</span>
                                        </Stack>
                                    </Stack>

                                    <Stack direction="row" alignItems="center">
                                        <PlayArrowIcon className={classes.contactIcon} />
                                        <Stack
                                            direction="row"
                                            alignItems="center"
                                            className={classes.contactItem}
                                            component={Link}
                                            href="https://drive.google.com/file/d/1PZqyPjd99-HmsTDXZOJzEVVJJIHMSO82/view?usp=sharing"
                                            style={{ paddingLeft: 5, }}
                                        >
                                            <span className={classes.iconPill} style={{ textJustify: 'center', paddingLeft: '-10px', }}>Demo</span>
                                        </Stack>
                                    </Stack>

                                    <Stack direction="row" alignItems="center">
                                        <MovieIcon className={classes.contactIcon} />
                                        <Stack
                                            direction="row"
                                            alignItems="center"
                                            className={classes.contactItem}
                                            component={Link}
                                            href="https://www.youtube.com/watch?v=an-oTVQzHT0"
                                            style={{ paddingLeft: 5, }}
                                        >
                                            <span className={classes.iconPill} style={{ textJustify: 'center', paddingLeft: '-10px', }}>Talk</span>
                                        </Stack>
                                    </Stack>
                                </Stack> */}
                            </CardContent>
                        </CardActionArea>
                    </Card>
                </Grid>
            </Grid>
        </Container>

        {/* <Container maxWidth="lg" sx={{ py: 6 }} style={{ paddingTop: 60, }}>
            <Typography variant="h4" className={classes.headNames}>
                Exciting Ongoing Work
            </Typography>

            <Grid container spacing={3} className="grid featured" style={{ paddingTop: 10, }}>
                <Grid item xs={12} sm={6} md={12}>
                    <Card elevation={2} sx={{ height: "100%" }}>
                        <CardActionArea
                            href=""
                            target="_blank"
                            rel="noreferrer"
                        >
                            <Box
                                sx={{
                                    width: "100%",
                                    display: "flex",
                                    justifyContent: "center",
                                    alignContent: 'center',
                                }}
                                >
                                <img
                                    src={require("./img/nl_wia.png")}
                                    alt="WhIf"
                                    style={{ width: "60%", }}
                                />
                            </Box>
                            <CardContent>
                                <Typography className={classes.title}>
                                    From Natural Language to Interactive What-if Analysis Interfaces: An Approach using Intermediate Representation
                                </Typography>
                                <Typography className={classes.authors}>
                                    <b className={classes.bold}>Sneha Gathani</b>, Dan Marshall, Çağatay Demiralp, Steven Drucker, Zhicheng Liu
                                </Typography>
                                <Typography className={classes.authorsHide}>This is hidden, this is hidden, this is hidden</Typography>
                                <Typography sx={{ fontSize: '16px', display: 'flex', alignItems: 'center' }} style={{ paddingTop: '5px', }}>
                                    <FindReplaceIcon sx={{ fontSize: '16px', color: '#DF825F', }} /><b style={{ fontSize: '18px', fontWeight: 'bold', marginLeft: '4px' }}>Under Review: CHI 2026</b>
                                </Typography>
                                <Typography className={classes.subtitle} style={{ paddingTop: '16px', }}>
                                    An approach translating natural language what-if questions to interactive visual interfaces via intermediate representation like Praxa Specification Language using LLMs and agentic workflows.
                                </Typography>
                            </CardContent>
                        </CardActionArea>
                    </Card>
                </Grid>
            </Grid>
        </Container> */}
        <div style={{ paddingTop: 30, }} />
    </main>
    );
}

export default App;
